import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { AlertTriangle, Clock } from 'lucide-react';

interface SessionExpirationWarningProps {
  showWarning: boolean;
  sessionExpired: boolean;
  warningTimeRemainingMs: number;
  onExtendSession: () => void;
  onLogout: () => void;
}

const SessionExpirationWarning: React.FC<SessionExpirationWarningProps> = ({
  showWarning,
  sessionExpired,
  warningTimeRemainingMs,
  onExtendSession,
  onLogout
}) => {
  const location = useLocation();
  const isOnLoginPage = location.pathname === '/login';

  const formatRemainingTime = (ms: number): string => {
    const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (sessionExpired && !isOnLoginPage) {
      console.log('[SessionExpirationWarning] Session expired, logging out and redirecting to login');

      const forceLogout = async () => {
        try {
          localStorage.removeItem('account-token');
          localStorage.removeItem('account-refresh-token');
          localStorage.removeItem('refreshToken');

          await onLogout();

          window.location.href = '/login';
        } catch (error) {
          console.error('[SessionExpirationWarning] Error during forced logout:', error);
          window.location.href = '/login';
        }
      };

      forceLogout();

      const fallbackTimer = setTimeout(() => {
        console.log('[SessionExpirationWarning] Fallback redirect to login');
        window.location.href = '/login';
      }, 2000);

      return () => clearTimeout(fallbackTimer);
    }
  }, [sessionExpired, onLogout, isOnLoginPage]);

  if (isOnLoginPage) {
    return null;
  }

  if (!showWarning && !sessionExpired) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border border-orange-200">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            {sessionExpired ? (
              <AlertTriangle className="h-8 w-8 text-red-500" />
            ) : (
              <Clock className="h-8 w-8 text-orange-500" />
            )}
          </div>

          <div className="flex-1">
            <h2 className="text-xl font-semibold text-gray-900 mb-3">
              {sessionExpired ? 'Session Expired' : 'Session Expiring Soon'}
            </h2>

            <div className="mb-6">
              <p className="text-gray-600 mb-3">
                {sessionExpired
                  ? 'Your session has expired for security reasons. You will be logged out and redirected to the login page.'
                  : 'Your session will expire due to inactivity. For your security, you will be automatically logged out when the timer reaches zero.'}
              </p>

              {!sessionExpired && (
                <div className="rounded-md bg-orange-50 border border-orange-200 px-4 py-3">
                  <p className="text-sm text-orange-800 mb-1 font-medium">
                    Time remaining
                  </p>
                  <p className="text-2xl font-semibold text-orange-700 tabular-nums">
                    {formatRemainingTime(warningTimeRemainingMs)}
                  </p>
                </div>
              )}
            </div>

            {!sessionExpired && (
              <div className="flex gap-3">
                <button
                  onClick={onExtendSession}
                  className="flex-1 bg-[#6BDBFF] hover:bg-[#57d3ff] text-gray-900 font-medium py-2.5 px-4 rounded-lg transition-colors"
                >
                  Stay Logged In
                </button>
                <button
                  onClick={onLogout}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2.5 px-4 rounded-lg transition-colors"
                >
                  Log Out Now
                </button>
              </div>
            )}

            {sessionExpired && (
              <div className="text-sm text-gray-500 text-center">
                Redirecting to login...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionExpirationWarning;